import { ShoppingBasketCoCoScope } from '../../common/enums.js';
import {
  getShoppingBasketFromLocalStorage,
  handleShoppingBasketUpdate,
  parseBasketJson,
} from '../../common/hooks/useShoppingBasketCoCo.js';
import {
  filterExtensionsWithValues,
  hasFixedBroadbandProductInBasket,
  isFixedBroadbandSubCategory,
} from '../../common/utils/productOfferingsUtils.js';
import { BASKET_ITEM_ADDED_EVENT } from '../HeaderShoppingCart/basketPopupEvents.js';
import type {
  BasketItemAddedDetailCoCo,
  ProductOfferingCartItems,
  ShoppingBasketType,
} from '../../common/types/shoppingBasketCoCo.js';

const qualificationAddressExtensionName = 'addressIdForQualification';
const qualificationAddressExtensionTarget = 'onlineApi/addressIdForQualification';

/**
 * Dispatch basket item added event with CoCo-specific detail including cartItemId.
 * Uses the same event name as the shared dispatchBasketItemAdded but includes cartItemId
 * to resolve the exact entry when the same offer exists multiple times in the basket.
 */
const dispatchBasketItemAddedCoCo = (detail: BasketItemAddedDetailCoCo): void => {
  window.dispatchEvent(new CustomEvent(BASKET_ITEM_ADDED_EVENT, { detail }));
};

export interface AddToShoppingBasketPropsCoCo {
  basketItem: ProductOfferingCartItems;
  count?: number;
}

export const addToBasket = ({
  basketItem,
  count = 1,
  scope = ShoppingBasketCoCoScope.ADMIN,
}: AddToShoppingBasketPropsCoCo & { scope?: ShoppingBasketCoCoScope }) => {
  const shoppingBasketJson: ShoppingBasketType = parseBasketJson(getShoppingBasketFromLocalStorage(scope));
  const quantity = count ?? 1;
  const subcategory = basketItem.productOfferingType?.subcategory ?? '';
  const isFixedBroadband = isFixedBroadbandSubCategory(subcategory);

  if (isFixedBroadband && (quantity > 1 || hasFixedBroadbandProductInBasket(shoppingBasketJson, basketItem.id))) {
    return;
  }

  // Always insert as separate entries — each gets a unique cartItemId.
  const newEntries: ProductOfferingCartItems[] = Array.from({ length: quantity }, () => ({
    ...basketItem,
    cartItemId: `cartItem-${crypto.randomUUID()}`,
  }));
  shoppingBasketJson.productOfferings = [...(shoppingBasketJson.productOfferings ?? []), ...newEntries];
  handleShoppingBasketUpdate(JSON.stringify(shoppingBasketJson), scope);
  // Keep the mini cart tied to the exact new entry when the same offer already exists in the basket.
  const detail: BasketItemAddedDetailCoCo = {
    id: basketItem.id,
    cartItemId: newEntries[0].cartItemId,
    quantity,
  };
  dispatchBasketItemAddedCoCo(detail);
};

// TODO: Function to add product to cart, will be used when integrating with Browse page
export const addToShoppingBasketCoCo = (
  productOffers: ProductOfferingCartItems,
  count = 1,
  qualificationAddressId?: string,
  qualificationAddressText?: string,
  scope: ShoppingBasketCoCoScope = ShoppingBasketCoCoScope.ADMIN
) => {
  const extensions =
    filterExtensionsWithValues(productOffers.extensions)?.filter(
      extension => !qualificationAddressId || extension.name !== qualificationAddressExtensionName
    ) ?? [];

  if (qualificationAddressId) {
    extensions.push({
      name: qualificationAddressExtensionName,
      target: qualificationAddressExtensionTarget,
      type: 'Integer',
      isMandatory: true,
      value: [{ value: qualificationAddressId }],
    });
  }

  addToBasket({
    basketItem: {
      ...productOffers,
      extensions: extensions.length > 0 ? extensions : undefined,
      qualificationAddressText,
    },
    count,
    scope,
  });
};
